import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { BaseApiServiceImpl } from "../app_utils/api/BaseApiServiceImpl";
import { formatAmountWithCommas } from "../app_utils/utils/Utils";
import { UserSessionUtils } from "../app_utils/utils/UserSessionUtils";
import { PrimeIcons } from "primereact/api";
import GatewayHealthBanner from "./components/GatewayHealthBanner";
import GatewayHealthCards from "./components/GatewayHealthCards";

const Dashboard = (props: any) => {
  const history = useHistory();
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [smsCredits, setSmsCredits] = useState<number>(0);
  const [emailCredits, setEmailCredits] = useState<number>(0);
  const [totalSms, setTotalSms] = useState<number>(0);
  const [totalEmails, setTotalEmails] = useState<number>(0);
  const [totalPayments, setTotalPayments] = useState<number>(0);
  const [activeGateways, setActiveGateways] = useState<number>(0);
  const [apiKeysCount, setApiKeysCount] = useState<number>(0);
  const [pendingPayments, setPendingPayments] = useState<number>(0);
  const [successfulPayments, setSuccessfulPayments] = useState<number>(0);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [healthData, setHealthData] = useState<any[]>([]);
  const [healthAlerts, setHealthAlerts] = useState<any[]>([]);
  const [isLoadingHealth, setIsLoadingHealth] = useState(true);

  const userId = UserSessionUtils.getUserDetails()?.id;
  const userName = UserSessionUtils.getUserDetails()?.firstName;
  const isAdmin = UserSessionUtils.isSuperAdmin();

  const fetchStats = useCallback(() => {
    setIsLoadingStats(true);

    Promise.all([
      new BaseApiServiceImpl("/api/v1/wallet-deposits/balance-info/" + userId)
        .getRequestWithJsonResponse({})
        .then((response) => ({
          balance: response?.data?.balance ?? 0,
          smsCredits: response?.data?.smsCredits ?? response?.data?.credits ?? 0,
          emailCredits: response?.data?.emailCredits ?? 0,
        }))
        .catch(() => ({ balance: 0, smsCredits: 0, emailCredits: 0 })),

      new BaseApiServiceImpl("/api/v1/sms/ui")
        .getRequestWithJsonResponse({ offset: 0, limit: 1 })
        .then((response) => response?.totalItems || response?.totalRecords || 0)
        .catch(() => 0)
        .then((total) => ({ total })),

      new BaseApiServiceImpl("/api/v1/messages/ui")
        .getRequestWithJsonResponse({ offset: 0, limit: 1 })
        .then((response) => response?.totalItems || 0)
        .catch(() => 0)
        .then((total) => ({ total })),

      new BaseApiServiceImpl("/api/v1/payments/ui")
        .getRequestWithJsonResponse({ offset: 0, limit: 1 })
        .then((response) => response?.totalItems || 0)
        .catch(() => 0)
        .then((total) => ({ total })),

      new BaseApiServiceImpl("/api/v1/gateways")
        .getRequestWithJsonResponse({ offset: 0, limit: 1, status: "Active" })
        .then((response) => response?.totalItems || 0)
        .catch(() => 0)
        .then((total) => ({ total })),

      new BaseApiServiceImpl("/api/v1/api-keys")
        .getRequestWithJsonResponse({ offset: 0, limit: 1 })
        .then((response) => response?.totalItems || 0)
        .catch(() => 0)
        .then((total) => ({ total })),

      new BaseApiServiceImpl("/api/v1/payments/ui")
        .getRequestWithJsonResponse({ offset: 0, limit: 1, status: "Pending" })
        .then((response) => response?.totalItems || 0)
        .catch(() => 0)
        .then((total) => ({ total })),

      new BaseApiServiceImpl("/api/v1/payments/ui")
        .getRequestWithJsonResponse({ offset: 0, limit: 1, status: "Completed" })
        .then((response) => response?.totalItems || 0)
        .catch(() => 0)
        .then((total) => ({ total })),
    ])
      .then(([walletData, smsData, emailData, paymentData, gatewayData, apiKeyData, pendingData, successData]) => {
        setWalletBalance(walletData.balance);
        setSmsCredits(walletData.smsCredits);
        setEmailCredits(walletData.emailCredits);
        setTotalSms(smsData.total);
        setTotalEmails(emailData.total);
        setTotalPayments(paymentData.total);
        setActiveGateways(gatewayData.total);
        setApiKeysCount(apiKeyData.total);
        setPendingPayments(pendingData.total);
        setSuccessfulPayments(successData.total);
        setIsLoadingStats(false);
      })
      .catch(() => {
        setIsLoadingStats(false);
      });
  }, [userId]);

  const handleBannerClick = useCallback(() => {
    history.push("/home");
  }, [history]);

  const handleAcknowledge = useCallback(async (alertId: number) => {
    try {
      await new BaseApiServiceImpl(`/api/v1/gateway-health/alerts/${alertId}/acknowledge`)
        .postRequestWithJsonResponse({});
      const alertsResponse = await new BaseApiServiceImpl("/api/v1/gateway-health/alerts")
        .getRequestWithJsonResponse({});
      setHealthAlerts(alertsResponse?.data || []);
    } catch {
      // error handled by BaseApiServiceImpl
    }
  }, []);

  useEffect(() => {
    if (userId) {
      fetchStats();
    }
  }, [userId, fetchStats]);

  useEffect(() => {
    if (!isAdmin) {
      setIsLoadingHealth(false);
      return;
    }
    setIsLoadingHealth(true);
    Promise.all([
      new BaseApiServiceImpl("/api/v1/gateway-health")
        .getRequestWithJsonResponse({})
        .then((response) => response?.data || [])
        .catch(() => []),
      new BaseApiServiceImpl("/api/v1/gateway-health/alerts")
        .getRequestWithJsonResponse({})
        .then((response) => response?.data || [])
        .catch(() => []),
    ])
      .then(([health, alerts]) => {
        setHealthData(health);
        setHealthAlerts(alerts);
        setIsLoadingHealth(false);
      })
      .catch(() => {
        setIsLoadingHealth(false);
      });
  }, [userId]);

  const StatCard = ({
    title,
    value,
    icon,
    iconColor,
    subtitle,
    gradient,
  }: {
    title: string;
    value: string | number;
    icon: string;
    iconColor?: string;
    subtitle?: string;
    gradient?: string;
  }) => (
    <div className="col-12 md:col-3">
      <div
        className="card"
        style={
          gradient
            ? { background: gradient, color: "white" }
            : undefined
        }
      >
        <div className="flex align-items-center justify-content-between">
          <div>
            <span
              style={{
                fontSize: "0.875rem",
                opacity: gradient ? 0.8 : undefined,
                color: gradient ? undefined : "#64748b",
              }}
            >
              {title}
            </span>
            <h3
              className="m-0 mt-2"
              style={{ color: gradient ? "white" : undefined }}
            >
              {value}
            </h3>
            {subtitle && (
              <span
                style={{
                  fontSize: "0.8rem",
                  opacity: gradient ? 0.7 : undefined,
                  color: gradient ? undefined : "#94a3b8",
                }}
              >
                {subtitle}
              </span>
            )}
          </div>
          <i
            className={icon}
            style={{
              fontSize: "2rem",
              opacity: gradient ? 0.8 : undefined,
              color: gradient ? undefined : iconColor,
            }}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="grid dashboard">
      <div className="col-12">
        <div className="flex align-items-center justify-content-between mb-3">
          <div>
            <h4 className="m-0">Welcome back, {userName || "User"}!</h4>
            <span className="text-500">
              Here&apos;s what&apos;s happening with your platform
            </span>
          </div>
        </div>
      </div>

      {isAdmin && (
        <div className="col-12">
          <GatewayHealthBanner alerts={healthAlerts} onAlertClick={handleBannerClick} onAcknowledge={handleAcknowledge} />
        </div>
      )}

      {isAdmin && (
        <div className="col-12">
          <GatewayHealthCards healthData={healthData} loading={isLoadingHealth} />
        </div>
      )}

      <StatCard
        title="Wallet Balance"
        value={formatAmountWithCommas(walletBalance, "UGX")}
        icon={PrimeIcons.WALLET}
        gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
        subtitle={`${smsCredits.toFixed(2)} SMS / ${emailCredits.toFixed(2)} Email credits`}
      />

      <StatCard
        title="SMS Credits"
        value={isLoadingStats ? "..." : smsCredits.toFixed(2)}
        icon={PrimeIcons.COMMENT}
        iconColor="#3b82f6"
        subtitle="Available"
      />

      <StatCard
        title="Email Credits"
        value={isLoadingStats ? "..." : emailCredits.toFixed(2)}
        icon={PrimeIcons.ENVELOPE}
        iconColor="#10b981"
        subtitle="Available"
      />

      <StatCard
        title="Total SMS"
        value={isLoadingStats ? "..." : totalSms}
        icon={PrimeIcons.ENVELOPE}
        iconColor="#6366f1"
        subtitle="All time"
      />

      <StatCard
        title="Total Emails"
        value={isLoadingStats ? "..." : totalEmails}
        icon={PrimeIcons.SEND}
        iconColor="#06b6d4"
        subtitle="All time"
      />

      <StatCard
        title="Total Payments"
        value={isLoadingStats ? "..." : totalPayments}
        icon={PrimeIcons.DOLLAR}
        iconColor="#f59e0b"
        subtitle="All time"
      />

      <StatCard
        title="Active Gateways"
        value={isLoadingStats ? "..." : activeGateways}
        icon={PrimeIcons.COMPASS}
        iconColor="#10b981"
        subtitle="Active"
      />

      <StatCard
        title="API Keys"
        value={isLoadingStats ? "..." : apiKeysCount}
        icon={PrimeIcons.KEY}
        iconColor="#8b5cf6"
        subtitle="Total"
      />

      <StatCard
        title="Pending Payments"
        value={isLoadingStats ? "..." : pendingPayments}
        icon={PrimeIcons.CLOCK}
        iconColor="#f59e0b"
        subtitle="Awaiting"
      />

      <StatCard
        title="Successful Payments"
        value={isLoadingStats ? "..." : successfulPayments}
        icon={PrimeIcons.CHECK_CIRCLE}
        iconColor="#22c55e"
        subtitle="Completed"
      />
    </div>
  );
};

export default Dashboard;
